// pages/book/BookRequest.tsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApiUrl, fetchWithAuth } from "../../api";
import { useAuthStore } from "../../store/authStore";

interface BookRequest {
  id: number;
  book_title: string;
  status: 0 | 1 | 2;
  created_at: string;
}

interface BookRequestDetail {
  id: number;
  book_title: string;
  reason: string | null;
  status: 0 | 1 | 2;
  created_at: string;
  updated_at: string;
}

const STATUS_MAP = {
  0: { label: "검토중", cls: "bg-[#fff3e0] text-[#f5a623]" },
  1: { label: "구비완료", cls: "bg-[#e8f5e9] text-[#4caf50]" },
  2: { label: "취소", cls: "bg-[#fdecea] text-[#e53935]" },
};

const PAGE_SIZE = 10;

export default function BookRequest() {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuthStore();
  const [list, setList] = useState<BookRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ book_title: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [modalItem, setModalItem] = useState<BookRequestDetail | null>(null);
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    if (!isLoggedIn) { alert("로그인이 필요한 메뉴입니다."); navigate("/auth/login"); return; }
    fetchList();
  }, [page]);

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(
        getApiUrl(`/marubit/api/book/request/my?page=${page}&limit=${PAGE_SIZE}`)
      );
      if (res.ok) {
        const data = await res.json();
        setList(data.data?.requests ?? []);
        setTotal(data.data?.total ?? 0);
      }
    } catch (e) {
      console.error("책 신청 목록 로딩 실패:", e);
    } finally {
      setLoading(false);
    }
  };

  const openModal = async (id: number) => {
    setModalLoading(true);
    setModalItem(null);
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/book/request/${id}`));
      if (res.ok) {
        const data = await res.json();
        setModalItem(data.data);
      }
    } catch (e) {
      console.error("상세 로딩 실패:", e);
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => setModalItem(null);

  const handleSubmit = async () => {
    setErrorMsg("");
    if (!form.book_title.trim()) { setErrorMsg("도서명을 입력해주세요."); return; }
    if (!form.reason.trim()) { setErrorMsg("신청 사유를 입력해주세요."); return; }

    setSubmitting(true);
    try {
      const res = await fetchWithAuth(getApiUrl("/marubit/api/book/request"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setForm({ book_title: "", reason: "" });
        setShowForm(false);
        setPage(1);
        await fetchList();
      } else {
        const data = await res.json();
        setErrorMsg(data.error?.message ?? "신청 중 오류가 발생했습니다.");
      }
    } catch (e) {
      setErrorMsg("서버 연결 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const getPageNumbers = () => {
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, start + 4);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const inputCls = "w-full border border-[#e5e7eb] rounded-[8px] px-3 py-[9px] text-[14px] text-[#3d2f2a] outline-none focus:border-[#887267] transition-colors bg-white";

  return (
    <div className="bg-[#fafafa] min-h-screen px-6 py-12">
      <div className="max-w-[1200px] mx-auto">

        {/* 타이틀 */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-[26px] font-extrabold text-[#5a4035] mb-2">책 신청</h1>
            <p className="text-[14px] text-[#6b5248] font-medium">읽고 싶은 도서를 신청해주세요.</p>
          </div>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 px-5 py-[10px] bg-[#887267] text-white text-[13px] font-semibold rounded-[8px] hover:bg-[#6b5248] transition-colors border-none cursor-pointer shrink-0"
            >
              <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="white" strokeWidth={2.5}>
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              책 신청하기
            </button>
          )}
        </div>

        {/* 신청 폼 */}
        {showForm && (
          <div className="bg-white rounded-2xl border border-[#f0ebe7] shadow-[0_2px_12px_rgba(136,114,103,0.08)] px-7 py-6 mb-6">
            <p className="m-0 mb-5 text-[15px] font-bold text-[#3d2f2a]">희망 도서 신청</p>
            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-[13px] font-semibold text-[#555] mb-1">도서명 <span className="text-red-400">*</span></label>
                <input className={inputCls} placeholder="도서명을 입력해주세요" value={form.book_title} onChange={e => setForm(f => ({ ...f, book_title: e.target.value }))} />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-[#555] mb-1">신청 사유 <span className="text-red-400">*</span></label>
                <textarea className={`${inputCls} resize-none h-[80px]`} placeholder="신청 사유를 입력해주세요" value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} />
              </div>
              {errorMsg && <p className="m-0 text-[13px] text-red-500">{errorMsg}</p>}
              <div className="flex gap-2 justify-end mt-1">
                <button onClick={() => { setShowForm(false); setErrorMsg(""); }} className="px-5 py-[9px] text-[13px] font-semibold text-gray-500 bg-[#f5f0ec] rounded-[8px] border-none cursor-pointer hover:bg-[#ede8e4] transition-colors">취소</button>
                <button onClick={handleSubmit} disabled={submitting} className="px-5 py-[9px] text-[13px] font-semibold text-white bg-[#887267] rounded-[8px] border-none cursor-pointer hover:bg-[#6b5248] transition-colors disabled:opacity-50">
                  {submitting ? "신청 중..." : "신청하기"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 신청 목록 */}
        {!showForm && (
          <>
            <div className="bg-white rounded-2xl border border-[#f0ebe7] shadow-[0_2px_12px_rgba(136,114,103,0.06)] overflow-hidden">
              <div className="px-6 py-4 border-b border-[#f5f0ec] bg-[#f0ebe7] flex items-center justify-between">
                <p className="m-0 text-[15px] font-bold text-[#3d2f2a]">신청 내역</p>
                <p className="m-0 text-[13px] text-gray-400">총 {total}건</p>
              </div>

              {loading ? (
                <div className="py-16 text-center text-[14px] text-gray-400">불러오는 중...</div>
              ) : list.length === 0 ? (
                <div className="py-16 text-center text-[14px] text-gray-400">신청 내역이 없습니다.</div>
              ) : (
                <div className="divide-y divide-[#f5f0ec]">
                  {list.map(item => {
                    const st = STATUS_MAP[item.status] ?? STATUS_MAP[0];
                    return (
                      <div
                        key={item.id}
                        onClick={() => openModal(item.id)}
                        className="px-6 py-4 hover:bg-[#fdf9f7] transition-colors cursor-pointer"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="m-0 text-[15px] font-bold text-[#3d2f2a] truncate">{item.book_title}</p>
                            <p className="m-0 text-[12px] text-gray-400 mt-1">{item.created_at.slice(0, 10)}</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className={`inline-block px-3 py-[3px] rounded-full text-[12px] font-bold ${st.cls}`}>
                              {st.label}
                            </span>
                            <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="#ccc" strokeWidth={2}>
                              <polyline points="9 18 15 12 9 6" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:border-[#887267] hover:text-[#887267] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >‹</button>
                {getPageNumbers().map(num => (
                  <button
                    key={num}
                    onClick={() => setPage(num)}
                    className={[
                      "w-9 h-9 flex items-center justify-center rounded-lg text-[14px] font-medium transition-colors",
                      num === page
                        ? "bg-[#887267] text-white border border-[#887267]"
                        : "border border-gray-200 text-gray-500 hover:border-[#887267] hover:text-[#887267]",
                    ].join(" ")}
                  >{num}</button>
                ))}
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:border-[#887267] hover:text-[#887267] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >›</button>
              </div>
            )}
          </>
        )}

      </div>

      {/* 모달 */}
      {(modalItem !== null || modalLoading) && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4" onClick={closeModal}>
          <div className="bg-white rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.16)] w-full max-w-[480px] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-[#887267] px-6 py-5 flex items-center justify-between">
              <p className="m-0 text-white text-[16px] font-bold">신청 상세</p>
              <button onClick={closeModal} className="w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center border-none cursor-pointer transition-colors">
                <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="white" strokeWidth={2.5}>
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            {modalLoading ? (
              <div className="py-12 text-center text-[14px] text-gray-400">불러오는 중...</div>
            ) : modalItem && (
              <div className="px-6 py-6 flex flex-col gap-4">
                <div>
                  <span className={`inline-block px-3 py-[3px] rounded-full text-[12px] font-bold ${(STATUS_MAP[modalItem.status] ?? STATUS_MAP[0]).cls}`}>
                    {(STATUS_MAP[modalItem.status] ?? STATUS_MAP[0]).label}
                  </span>
                </div>
                <div className="divide-y divide-[#f5f0ec]">
                  {[
                    { label: "도서명", value: modalItem.book_title },
                    { label: "신청 사유", value: modalItem.reason ?? "-" },
                    { label: "신청일", value: modalItem.created_at.slice(0, 10) },
                    { label: "최종 수정일", value: modalItem.updated_at.slice(0, 10) },
                  ].map(row => (
                    <div key={row.label} className="py-3 flex gap-4">
                      <span className="w-[90px] shrink-0 text-[13px] text-gray-400 font-medium">{row.label}</span>
                      <span className="text-[14px] text-[#3d2f2a] font-medium leading-relaxed">{row.value}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end mt-2">
                  <button onClick={closeModal} className="px-5 py-[9px] text-[13px] font-semibold text-white bg-[#887267] rounded-[8px] border-none cursor-pointer hover:bg-[#6b5248] transition-colors">닫기</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}